// Server-side beautiful PDF for LXP simulation certificates.
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export const generateLxpCertificatePdf = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z.object({ certificateId: z.string().uuid() }).parse(i),
  )
  .handler(async ({ data, context }) => {
    const { supabase } = context;
    const { data: cert, error } = await supabase
      .from("lxp_certificates")
      .select(
        "id, user_id, certificate_number, verification_token, full_name, final_score, issued_at, simulation:lxp_simulations(title, practice_area, slug)",
      )
      .eq("id", data.certificateId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!cert) throw new Error("Certificate not found");

    const sim: any = (cert as any).simulation ?? {};
    const simTitle: string = sim.title ?? "Legal Simulation";
    const practiceArea: string = sim.practice_area ?? "";

    const { jsPDF } = await import("jspdf");
    const QRCode = await import("qrcode");
    const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "landscape" });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();

    const NAVY: [number, number, number] = [11, 31, 58];
    const EMERALD: [number, number, number] = [16, 122, 87];
    const GOLD: [number, number, number] = [201, 161, 74];
    const INK: [number, number, number] = [30, 30, 30];
    const MUTED: [number, number, number] = [108, 108, 108];
    const CREAM: [number, number, number] = [253, 251, 245];

    // Cream background
    doc.setFillColor(...CREAM);
    doc.rect(0, 0, pageW, pageH, "F");

    // Decorative navy corners
    doc.setFillColor(...NAVY);
    doc.triangle(0, 0, 140, 0, 0, 140, "F");
    doc.triangle(pageW, pageH, pageW - 140, pageH, pageW, pageH - 140, "F");

    // Emerald accents
    doc.setFillColor(...EMERALD);
    doc.triangle(pageW, 0, pageW - 110, 0, pageW, 110, "F");
    doc.triangle(0, pageH, 110, pageH, 0, pageH - 110, "F");

    // Gold double frame
    doc.setDrawColor(...GOLD);
    doc.setLineWidth(2.2);
    doc.rect(28, 28, pageW - 56, pageH - 56);
    doc.setLineWidth(0.6);
    doc.rect(38, 38, pageW - 76, pageH - 76);

    // Top ribbon
    doc.setFillColor(...NAVY);
    doc.rect(38, 38, pageW - 76, 56, "F");
    doc.setTextColor(...GOLD);
    doc.setFont("times", "bold");
    doc.setFontSize(11);
    doc.text("SA LEXPRACTIQ · LEGAL EXPERIENCE PLATFORM", pageW / 2, 62, { align: "center" });
    doc.setTextColor(255, 255, 255);
    doc.setFont("times", "italic");
    doc.setFontSize(9);
    doc.text("Certified Practical Legal Simulation", pageW / 2, 80, { align: "center" });

    // Stars flourish
    doc.setTextColor(...GOLD);
    doc.setFont("times", "bold");
    doc.setFontSize(14);
    doc.text("★  ★  ★", pageW / 2, 128, { align: "center" });

    // Title
    doc.setTextColor(...NAVY);
    doc.setFont("times", "bold");
    doc.setFontSize(38);
    doc.text("Certificate of Completion", pageW / 2, 168, { align: "center" });

    doc.setDrawColor(...GOLD);
    doc.setLineWidth(1.2);
    doc.line(pageW / 2 - 90, 180, pageW / 2 + 90, 180);

    // Presented to
    doc.setFont("times", "italic");
    doc.setFontSize(13);
    doc.setTextColor(...MUTED);
    doc.text("This is proudly presented to", pageW / 2, 210, { align: "center" });

    // Recipient
    doc.setFont("times", "bold");
    doc.setFontSize(32);
    doc.setTextColor(...INK);
    doc.text(cert.full_name, pageW / 2, 250, { align: "center" });
    doc.setDrawColor(210, 210, 210);
    doc.setLineWidth(0.4);
    doc.line(pageW / 2 - 230, 260, pageW / 2 + 230, 260);

    // Body
    doc.setFont("times", "normal");
    doc.setFontSize(12);
    doc.setTextColor(...INK);
    doc.text("for the successful completion of the practical legal simulation:", pageW / 2, 286, { align: "center" });

    doc.setFont("times", "bold");
    doc.setFontSize(18);
    doc.setTextColor(...NAVY);
    const titleLines = doc.splitTextToSize(simTitle, pageW - 260) as string[];
    let ty = 314;
    for (const ln of titleLines) {
      doc.text(ln, pageW / 2, ty, { align: "center" });
      ty += 22;
    }

    if (practiceArea) {
      doc.setFont("times", "italic");
      doc.setFontSize(11);
      doc.setTextColor(...EMERALD);
      doc.text(`Practice Area · ${practiceArea}`, pageW / 2, ty + 6, { align: "center" });
      ty += 18;
    }

    if (cert.final_score != null) {
      doc.setFont("times", "bold");
      doc.setFontSize(12);
      doc.setTextColor(...GOLD);
      doc.text(`Final Assessment Score: ${Number(cert.final_score).toFixed(1)}%`, pageW / 2, ty + 12, { align: "center" });
    }

    // Metadata block (left)
    const issuedDate = new Date(cert.issued_at).toLocaleDateString("en-ZA", {
      year: "numeric", month: "long", day: "numeric",
    });
    doc.setFont("times", "normal");
    doc.setFontSize(10.5);
    doc.setTextColor(...INK);
    doc.text(`Issued on ${issuedDate}`, 70, pageH - 150);
    doc.setFont("times", "bold");
    doc.text(`Certificate No: ${cert.certificate_number}`, 70, pageH - 132);
    doc.setFont("times", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...MUTED);

    const origin = "https://nkadimengdannylcs.com";
    const verifyUrl = `${origin}/lexpractiq/verify/${encodeURIComponent(cert.verification_token)}`;
    doc.text(`Verify at: ${verifyUrl}`, 70, pageH - 116);

    // QR
    try {
      const qrDataUrl = await QRCode.toDataURL(verifyUrl, {
        errorCorrectionLevel: "M", margin: 1, width: 512,
        color: { dark: "#0B1F3A", light: "#FDFBF5" },
      });
      const qrSize = 96;
      doc.addImage(qrDataUrl, "PNG", pageW - 96 - qrSize, pageH - 170, qrSize, qrSize);
      doc.setFontSize(8);
      doc.setTextColor(...MUTED);
      doc.text("Scan to verify", pageW - 96 - qrSize + qrSize / 2, pageH - 66, { align: "center" });
    } catch {}

    // Signature block
    doc.setDrawColor(...INK);
    doc.setLineWidth(0.6);
    doc.line(70, pageH - 88, 280, pageH - 88);
    doc.setFont("times", "italic");
    doc.setFontSize(10);
    doc.setTextColor(...NAVY);
    doc.text("Danny Nkadimeng", 70, pageH - 76);
    doc.setFont("times", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(...MUTED);
    doc.text("Founder — SA LexPractiq · NDLCS Academy", 70, pageH - 64);

    // Disclaimer
    doc.setFont("times", "italic");
    doc.setFontSize(7.5);
    doc.setTextColor(...MUTED);
    const disclaimer =
      "SA LexPractiq certifies completion of an AI-assessed practical legal simulation. " +
      "This credential is not a formal qualification, licence to practise law, or SAQA/NQF-accredited award.";
    const dl = doc.splitTextToSize(disclaimer, pageW - 160) as string[];
    doc.text(dl, pageW / 2, pageH - 46, { align: "center" });

    const buf = doc.output("arraybuffer") as ArrayBuffer;
    const bytes = new Uint8Array(buf);
    let bin = "";
    const CHUNK = 0x8000;
    for (let i = 0; i < bytes.length; i += CHUNK) {
      bin += String.fromCharCode(...(bytes.subarray(i, i + CHUNK) as unknown as number[]));
    }
    const base64 = btoa(bin);
    const safe = cert.certificate_number.replace(/[^A-Za-z0-9_-]/g, "_");
    return {
      base64,
      filename: `SA-LexPractiq-Certificate-${safe}.pdf`,
      verification_token: cert.verification_token,
    };
  });
